/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import axiosRetry from 'axios-retry';

import XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
// import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';

import { Button } from 'react-bootstrap';
import { Upload, message, Divider, Radio, Popover, Alert } from 'antd';
import { InboxOutlined, InfoCircleOutlined } from '@ant-design/icons';

import { computeSpdxData } from 'components/sections/fileInput';
import { convertMetadata, getLuoxData, convertLuoxData } from 'utils';
import SampleExcel from 'data/SpectralCalculator-Library-Excel-Input.xlsx';

import {
  setLibraryData,
  clearLibraryData,
  setGlobalSpinner,
  setGlobalToast,
  setExcelUploadErrors,
  setShowExcelUploadErrors,
} from 'redux/actions';

const { Dragger } = Upload;

const MultipleCalc = ({ shouldClearOnCalculate, closeAddLibraryModal }) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const [fileUpload, setFileUpload] = useState(null);
  const [fileUploadType, setFileUploadType] = useState('xlsx');

  const finishFileUpload = (libraryData) => {
    dispatch(setLibraryData(libraryData));
    setFileUpload([]);
    setFileUploadType('xlsx');
    closeAddLibraryModal && closeAddLibraryModal(false);
    // setExcelUploadSpinner(false);
    dispatch(setGlobalSpinner(false));
    if (fileUploadType === 'xlsx') {
      dispatch(setGlobalToast('success'));
    } else {
      history.push('/user-library');
    }
  };

  const importJsonLibrary = () => {
    dispatch(setGlobalSpinner('initialize'));
    var fileReader = new FileReader();
    fileReader.readAsText(fileUpload[0]);
    fileReader.onload = (event) => {
      const newLibraryJson = JSON.parse(event.target.result);
      const importedLibraryJson = Array.isArray(newLibraryJson)
        ? newLibraryJson
        : [newLibraryJson];
      // const changeIndexingOfImportedLibrary =
      // importedLibraryJson.forEach((data) => {
      //   Object.assign(data, { id: data.id + libraryData.length });
      // });
      finishFileUpload(importedLibraryJson);
    };
  };

  const uploadExcel = () => {
    dispatch(setGlobalSpinner('initialize'));
    const reader = new FileReader();
    reader.readAsBinaryString(fileUpload[0]);
    reader.onload = (e) => {
      const bstr = e.target.result;
      const wb = XLSX.read(bstr, {
        type: 'binary',
        bookVBA: true,
      });

      // Direct conversion from sheet to json
      // wb.SheetNames.forEach((sheet) => {
      //   let rowObject = XLSX.utils.sheet_to_row_object_array(wb.Sheets[sheet]);
      // });

      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];

      setTimeout(() => {
        if (ws && window.Worker) {
          dispatch(setGlobalToast('info'));
          dispatch(setGlobalSpinner('globalSpin'));
          const worker = new Worker('convertExcelToJson-worker.js');
          worker.postMessage({ ws });
          worker.onerror = (err) => err;
          worker.onmessage = (e) => {
            const { jsonData } = e.data;
            if (jsonData === 'corrupt') {
              alert(
                "Uploaded excel doesn't match the requirements. Please refer to the 'Add new SPD(s) to the User Library' modal for downloading sample excel"
              );
              dispatch(setGlobalSpinner(false));
            } else {
              calculateResults(jsonData);
            }
            worker.terminate(); // this line terminates the worker
          };
        }
      }, 2000);
    };
  };

  const getTm30Data = async (spd, wavelength) => {
    // axiosRetry(axios, {
    //   retries: 2,
    //   retryDelay: 1000,
    // });
    const response = await axios.post(
      `${window.location.origin}/api/calculate`,
      // `http://localhost:5000/api/calculate`,
      {
        spd: spd,
        wavelength: wavelength,
      }
    );
    const data = await response.data;
    return data;
  };

  const calculateResults = (jsonData) => {
    let excelLibraryData = [];
    let errorCount = 0;
    let excelUploadErrors = [];

    Object.keys(jsonData).every(async (key, index) => {
      let failedPrelimenaryChecks = false;

      const spd = jsonData[key];
      // 13 - Excel sheet row number of luox calculation specification
      const luoxDataType = spd.find(
        (value) => value.key.match(/\d+/)[0] === '13'
      )?.value;
      // 15 - Excel sheet row number of start wavelength
      const wavelengthStart = spd.find(
        (value) => value.key.match(/\d+/)[0] === '15'
      )?.value;
      // 16 - Excel sheet row number of end wavelength
      const wavelengthEnd = spd.find(
        (value) => value.key.match(/\d+/)[0] === '16'
      )?.value;
      // 17 - Excel sheet row number of interval
      const step = spd.find(
        (value) => value.key.match(/\d+/)[0] === '17'
      )?.value;
      // 1 to 12 - Excel sheet row numbers of metadata
      const infoData = spd?.filter(
        (value) =>
          value.key.match(/\d+/)[0] >= 1 && value.key.match(/\d+/)[0] <= 12
      );
      // 18 and above - Excel sheet row numbers of spd data
      const spdValuesObj = spd?.filter(
        (value) => value.key.match(/\d+/)[0] >= 18
      );
      const { metaData } = convertMetadata(infoData);

      let wavelengths = [];
      let excelUploadWarnings = [];

      // Prelimenary checks
      if (wavelengthStart > 400) {
        failedPrelimenaryChecks = true;
        excelUploadErrors.push({
          identifier: metaData.identifier,
          error: 'Start wavelength value cannot exceed 400',
        });
      }

      if (wavelengthEnd < 700) {
        failedPrelimenaryChecks = true;
        excelUploadErrors.push({
          identifier: metaData.identifier,
          error: 'End wavelength value cannot be less than 700',
        });
      }

      if (step < 1 || step > 5) {
        failedPrelimenaryChecks = true;
        excelUploadErrors.push({
          identifier: metaData.identifier,
          error: 'Cannot perform calculations above step size 5.',
        });
      }

      if (!wavelengthStart || !wavelengthEnd || !step) {
        failedPrelimenaryChecks = true;
        excelUploadErrors.push({
          identifier: metaData.identifier,
          error:
            'Excel upload requires use of a pre-formatted file that is available for download. Please add all the required data: start/end wavelengths, interval, and SPD. These fields, marked in red in the template, are required for the calculator to work. Metadata is optional but recommended to enhance sorting of the library.',
        });
      }

      if (!failedPrelimenaryChecks) {
        let spd_length = wavelengthEnd - wavelengthStart + 1;

        for (let i = 0; i < spd_length; i += parseInt(step)) {
          const newWavelength = i + parseInt(wavelengthStart);

          if (newWavelength >= 380 && newWavelength <= 780) {
            wavelengths.push(i + parseInt(wavelengthStart));
          }
        }

        let spdValues = spdValuesObj.map((obj) => obj['value']);

        // Clean up calculations input
        if (wavelengthStart < 380 || wavelengthEnd > 780) {
          excelUploadWarnings.push(
            'The required wavelength range is 380 - 780 nm. Data outside this range has not been used in the calculation.'
          );
          if (wavelengthStart < 380) {
            const spdsToRemoveFromStart = Math.round(
              (380 - wavelengthStart) / step
            );
            spdValues.splice(0, spdsToRemoveFromStart);
          }
          if (wavelengthEnd > 780) {
            const spdsToRemoveFromEnd = Math.round(
              (wavelengthEnd - 780) / step
            );
            spdValues.splice(spdValues.length - spdsToRemoveFromEnd);
          }
        }

        // Adding zeros if there is missing data
        if (wavelengthStart > 380 || wavelengthEnd < 780) {
          excelUploadWarnings.push(
            'The required wavelength range is 380 - 780 nm. Missing data has been replaced by zeros in the calculation.'
          );
          if (wavelengthStart > 380) {
            let newSpdWavelengthsToAdd = [];
            for (let i = 380; i < wavelengthStart; i += parseInt(step)) {
              newSpdWavelengthsToAdd.push(i);
            }
            wavelengths = [...newSpdWavelengthsToAdd, ...wavelengths];
            const addZerosToSpdValues = newSpdWavelengthsToAdd.map(
              (item) => '0'
            );
            spdValues = [...addZerosToSpdValues, ...spdValues];
          }

          if (wavelengthEnd < 780) {
            let newSpdWavelengthsToAdd = [];
            for (
              let i = parseInt(wavelengthEnd) + parseInt(step);
              i <= 780;
              i += parseInt(step)
            ) {
              newSpdWavelengthsToAdd.push(i);
            }
            wavelengths = [...wavelengths, ...newSpdWavelengthsToAdd];
            const addZerosToSpdValues = newSpdWavelengthsToAdd.map(
              (item) => '0'
            );
            spdValues = [...spdValues, ...addZerosToSpdValues];
          }
        }

        // Calculating Luox
        let luoxData = {};
        let convertedLuoxData = {};
        let luoxUnits = {
          absoluteOrRelative: 'absolute',
          radianceOrIrradiance: 'irradiance',
          powerScale: 'mW',
          areaScale: 'm2',
          relativePower: 1,
        };
        if (luoxDataType !== undefined) {
          luoxData = getLuoxData(wavelengths, spdValues);
          convertedLuoxData = convertLuoxData(luoxData.luoxInput, luoxUnits);
        }

        // Check if both arrays match
        if (spdValues.length === wavelengths.length) {
          try {
            const data = await getTm30Data(spdValues, wavelengths);

            if (data.error) {
              excelUploadErrors.push({
                identifier: metaData.identifier,
                error: data.error,
              });
              errorCount = errorCount + 1;
            } else {
              let tm30Data = data.data.tm30;
              // Error/Warning Handling
              const duvTest = tm30Data.Numerical_Results.DUV[0].toFixed(4);
              const cctTest = tm30Data.Numerical_Results.CCT[0].toFixed(4);
              if (
                duvTest > 0.0091 ||
                duvTest < -0.018 ||
                cctTest < 2136 ||
                cctTest > 7042
              ) {
                excelUploadWarnings.push(
                  'The chromaticity is outside the range specified in ANSI C78.377-2017. Interpret results with caution.'
                );
              }

              const newEntry = {
                id: index + 1,
                metadata: metaData,
                tm30: tm30Data,
                luoxData: { ...convertedLuoxData, luoxUnits: luoxUnits },
                warnings: excelUploadWarnings,
              };

              excelLibraryData.push(newEntry);
              excelLibraryData.sort((a, b) => a.id - b.id);
            }
          } catch (e) {
            console.log(e);
            excelUploadErrors.push({
              identifier: metaData.identifier,
              error: e.message,
            });
            errorCount = errorCount + 1;
            // dispatch(setGlobalToast('fail'));
            dispatch(setGlobalSpinner(false));
          } finally {
            // Determining the end of execution
            if (
              Object.keys(jsonData).length ===
              excelLibraryData.length + errorCount
            ) {
              finishFileUpload(excelLibraryData);
              dispatch(setExcelUploadErrors(excelUploadErrors));
              if (excelUploadErrors.length > 0) {
                dispatch(setShowExcelUploadErrors('show'));
              }
            }
          }
        } else {
          errorCount = errorCount + 1;
          excelUploadErrors.push({
            identifier: metaData.identifier,
            error:
              'Size of wavelengths and SPD values do not match. Please make sure you provide the correct set of values.',
          });
        }
      } else {
        errorCount = errorCount + 1;
        dispatch(setGlobalSpinner(false));
      }
    });
  };

  const handleMultipleSpdxUpload = () => {
    dispatch(setGlobalSpinner('initialize'));
    let newLibraryData = [];
    let errorCount = 0;
    let excelUploadErrors = [];

    fileUpload.forEach((file, index) => {
      computeSpdxData(file.originFileObj).then(async (result) => {
        let excelUploadWarnings = [];
        const { spd, wavelength, metadata } = result;
        if (typeof result === 'string') {
          alert(
            ` ${result} Please make sure the uploaded file is in correct format. You can click on the info icon for more information.`
          );
          return;
        } else if (spd.length !== 401 || wavelength.length !== 401) {
          alert(
            ` Please make sure the uploaded file has sufficient spd and wavelength data for calculation.`
          );
          return;
        }

        // Check if both arrays match
        if (spd.length === wavelength.length) {
          try {
            const data = await getTm30Data(spd, wavelength);

            if (data.error) {
              excelUploadErrors.push({
                identifier: metadata.identifier,
                error: data.error,
              });
              errorCount = errorCount + 1;
            } else {
              let tm30Data = data.data.tm30;
              // Error/Warning Handling
              const duvTest = tm30Data.Numerical_Results.DUV[0].toFixed(4);
              const cctTest = tm30Data.Numerical_Results.CCT[0].toFixed(4);
              if (
                duvTest > 0.0091 ||
                duvTest < -0.018 ||
                cctTest < 2136 ||
                cctTest > 7042
              ) {
                excelUploadWarnings.push(
                  'The chromaticity is outside the range specified in ANSI C78.377-2017. Interpret results with caution.'
                );
              }

              const newEntry = {
                id: index + 1,
                metadata: metadata ? metadata : {},
                tm30: tm30Data,
                warnings: excelUploadWarnings,
              };
              newLibraryData.push(newEntry);
              // if (newLibraryData.length === fileUpload.length) {
              //   finishFileUpload(newLibraryData);
              // }
            }
          } catch (e) {
            console.log(e);
            excelUploadErrors.push({
              identifier: metadata.identifier,
              error: e.message,
            });
            errorCount = errorCount + 1;
            dispatch(setGlobalSpinner(false));
          } finally {
            // Determining the end of execution
            if (fileUpload.length === newLibraryData.length + errorCount) {
              finishFileUpload(newLibraryData);
              dispatch(setExcelUploadErrors(excelUploadErrors));
              if (excelUploadErrors.length > 0) {
                dispatch(setShowExcelUploadErrors('show'));
              }
            }
          }
        } else {
          errorCount = errorCount + 1;
          dispatch(setGlobalSpinner(false));
        }
      });
    });
  };

  const handleFileUpload = () => {
    closeAddLibraryModal && closeAddLibraryModal(false);
    if (shouldClearOnCalculate) {
      dispatch(clearLibraryData());
    }

    setTimeout(() => {
      switch (fileUploadType) {
        case 'xlsx':
          uploadExcel();
          break;
        case 'json':
          importJsonLibrary();
          break;
        case 'spdx':
          handleMultipleSpdxUpload();
          break;
        default:
          break;
      }
    }, 500);
  };

  const handleFileUploadTypeChange = (e) => {
    setFileUploadType(e.target.value);
    setFileUpload([]);
  };

  const fileUploadProps = {
    name: 'file',
    multiple: fileUploadType === 'spdx',
    action: null,
    accept: `.${fileUploadType}`,
    beforeUpload: (file) => {
      const fileNameSplitArray = file.name?.split('.');
      if (fileNameSplitArray) {
        let fileType;
        if (fileNameSplitArray === 2) {
        } else {
          fileType = fileNameSplitArray[fileNameSplitArray?.length - 1];
        }
        if (fileType !== fileUploadType) {
          message.error(`${file.name} is not a text file`);
        }
        return false;
      }
    },
    onChange(info) {
      const fileNameSplitArray = info.file.name?.split('.');
      if (fileNameSplitArray) {
        let fileType;
        if (fileNameSplitArray === 2) {
        } else {
          fileType = fileNameSplitArray[fileNameSplitArray?.length - 1];
        }
        if (fileType === fileUploadType) {
          if (fileType === 'spdx') {
            setFileUpload(info.fileList);
          } else {
            setFileUpload([info.file]);
          }
        }
      }
      if (info.fileList.length === 0) {
        setFileUpload(info.fileList);
      }
    },
  };

  const fileUploadGuide = (
    <div style={{ width: '50rem' }}>
      <h5>XLSX</h5>
      <p>
        Excel upload requires use of a pre-formatted file that is available for
        download. Please add all the required data: start/end wavelengths,
        interval, and SPD. These fields, marked in red in the template, are
        required for the calculator to work. Metadata is optional but
        recommended to enhance sorting of the library.
      </p>
      <h5>JSON</h5>
      <p>
        {/* 
        and make sure you have entered data in a proper format. The JSON input
        file has an array of "Library Data" with all the calculated data which
        is required for the upload to work and an optional array of "Library
        Info" which includes metadata. */}
        You can only upload JSON files that are generated by this web tool.
      </p>
      <h5>SPDX</h5>
      <p>
        Make sure to follow the layout specified in IES TM-27 as well as proper
        XML guidelines. You can find some resources in links below:
        <br />
        <a
          href='https://www.w3.org/TR/xmlschema11-1/'
          target='_blank'
          rel='noopener noreferrer'
        >
          https://www.w3.org/TR/xmlschema11-1/
        </a>
        <a
          href='https://en.wikipedia.org/wiki/XML#Key_terminology'
          target='_blank'
          rel='noopener noreferrer'
        >
          https://en.wikipedia.org/wiki/XML#Key_terminology
        </a>
      </p>
    </div>
  );

  const fileUploader = (
    <div>
      <div style={{ margin: '2rem 0' }}>
        <label>
          Select the type of file to upload:{' '}
          <Popover
            placement='right'
            content={fileUploadGuide}
            title='File upload instructions'
            trigger='click'
          >
            <InfoCircleOutlined />
          </Popover>
        </label>
        <br />
        <Radio.Group
          onChange={handleFileUploadTypeChange}
          value={fileUploadType}
        >
          <Radio value='xlsx'>
            XLSX (
            <a
              target='_blank'
              onClick={() =>
                saveAs(
                  SampleExcel,
                  'SpectralCalculator-Library-Excel-Input.xlsx'
                )
              }
              css={styles.sampleExcelLink}
            >
              Download Excel input template
            </a>
            )
          </Radio>
          <Radio value='json'>JSON</Radio>
          <Radio value='spdx'>SPDX (Multiple)</Radio>
        </Radio.Group>
      </div>
      {fileUploadType === 'spdx' && (
        <Fragment>
          <Alert
            message='Cannot upload more than 10 files at a time'
            type='warning'
            closable
          />
          <br />
          <strong>
            Note: .spdx upload only does TM-30, not alpha opic calculations
          </strong>
        </Fragment>
      )}
      <br />
      <br />
      <Dragger
        fileList={fileUpload}
        {...fileUploadProps}
        maxCount={fileUploadType === 'spdx' ? 10 : 1}
      >
        <p className='ant-upload-drag-icon'>
          <InboxOutlined />
        </p>
        <p className='ant-upload-text'>
          Click or drag file to this area to upload
        </p>
      </Dragger>
      <Button
        css={styles.jsonCalculateButton}
        type='submit'
        onClick={handleFileUpload}
        bsStyle='primary'
        disabled={fileUpload === null || fileUpload.length === 0}
      >
        Load
      </Button>
    </div>
  );

  return (
    <div>
      <div>
        <p css={styles.userLibraryNote}>
          The User Library is not saved if you leave the TM-30 web application.
          If you wish to maintain a copy for later use or to share with others,
          be sure to download the JSON file when you are finished.
        </p>
        <Divider />
        {fileUploader}
      </div>
    </div>
  );
};

export default MultipleCalc;

MultipleCalc.propTypes = {
  shouldClearOnCalculate: PropTypes.bool,
  dataReducers: PropTypes.object,
};

const styles = {
  userLibraryNote: css`
    font-weight: 400;
  `,
  sampleExcelLink: css`
    cursor: pointer;
  `,
  sampleExcelContainer: css`
    margin-bottom: 10px;
  `,
  excelCalculateButton: css`
    margin: 10px;
  `,
  jsonCalculateButton: css`
    margin: 10px 0px;
  `,
  loadingText: css`
    margin: 15px;
  `,
  helpBlock: css`
    font-size: 14px;
  `,
};
